import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Target, TrendingUp, AlertTriangle, CheckCircle, DollarSign, Wallet, Info, Percent } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useCostAnalysis, useBreakEvenImpact } from '@/hooks/finance/useCostAnalysis';
import { cn } from '@/lib/utils';

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

export default function CostAnalysis() {
  const { data: analysis, isLoading } = useCostAnalysis();
  const breakEvenImpact = useBreakEvenImpact(10000);

  if (isLoading) {
    return (
      <FinancePageLayout>
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-[200px]" />
          ))}
        </div>
      </FinancePageLayout>
    );
  }

  if (!analysis) {
    return (
      <FinancePageLayout>
        <Card>
          <CardContent className="py-10">
            <p className="text-center text-muted-foreground">
              Dados insuficientes para análise de custos
            </p>
          </CardContent>
        </Card>
      </FinancePageLayout>
    );
  }

  const totalCosts = analysis.total;
  const fixedPercent = totalCosts.total > 0 ? (totalCosts.fixed / totalCosts.total) * 100 : 0;
  const variablePercent = totalCosts.total > 0 ? (totalCosts.variable / totalCosts.total) * 100 : 0;

  const isSafe = analysis.safetyMargin >= 20;
  const hasHighLeverage = analysis.operationalLeverage > 3;

  // Cash runway status (with safe defaults)
  const cashRunway = analysis.cashRunwayMonths ?? 0;
  const runwayStatus = cashRunway >= 6 
    ? 'healthy' 
    : cashRunway >= 3 
      ? 'warning' 
      : 'critical';

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Análise Fixo vs Variável</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-slate-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custos Fixos</CardTitle>
            <div className="w-3 h-3 rounded-full bg-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalCosts.fixed)}
            </div>
            <p className="text-xs text-muted-foreground">
              {fixedPercent.toFixed(1)}% do total • Salários + Despesas fixas
            </p>
          </CardContent>
        </Card>

        <Card className="bg-teal-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              Custos Variáveis
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Calculado sobre receita RECEBIDA, não vendida</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <div className="w-3 h-3 rounded-full bg-teal-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalCosts.variable)}
            </div>
            <p className="text-xs text-muted-foreground">
              {variablePercent.toFixed(1)}% do total • Comissões + Variáveis
            </p>
          </CardContent>
        </Card>

        <Card className="bg-purple-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              Comissões
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{(analysis.commissionRate * 100).toFixed(0)}% sobre receita recebida</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <Percent className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">
              {formatCurrency(analysis.commissionCost)}
            </div>
            <p className="text-xs text-muted-foreground">
              {(analysis.commissionRate * 100).toFixed(0)}% × {formatCurrency(analysis.receivedRevenue)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Custos</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalCosts.total)}
            </div>
            <p className="text-xs text-muted-foreground">
              Por mês
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cost Distribution Bar */}
      <Card>
        <CardHeader>
          <CardTitle>Distribuição de Custos</CardTitle>
          <CardDescription>
            Salários base são custos fixos • Comissões são variáveis (baseadas no recebido)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-8 rounded-lg overflow-hidden flex">
            <div 
              className="bg-slate-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${fixedPercent}%` }}
            >
              {fixedPercent > 10 && 'Fixo'}
            </div>
            <div 
              className="bg-teal-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${variablePercent}%` }}
            >
              {variablePercent > 10 && 'Variável'}
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Fixo: {fixedPercent.toFixed(1)}%</span>
            <span>Variável: {variablePercent.toFixed(1)}%</span>
          </div>
        </CardContent>
      </Card>

      {/* Break-even, Cash Analysis and Risk Indicators */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Ponto de Equilíbrio
            </CardTitle>
            <CardDescription>
              Receita mínima <strong>recebida</strong> para cobrir custos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-3xl font-bold text-primary">
              {formatCurrency(analysis.breakEven)}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Receita Recebida (mês)</span>
                <span className="font-medium">{formatCurrency(analysis.receivedRevenue)}</span>
              </div>
              <Progress 
                value={analysis.breakEven > 0 ? Math.min((analysis.receivedRevenue / analysis.breakEven) * 100, 100) : 0} 
                className="h-3"
              />
              <div className={cn(
                "flex items-center gap-2 text-sm",
                analysis.receivedRevenue >= analysis.breakEven ? "text-green-600" : "text-yellow-600"
              )}>
                {analysis.receivedRevenue >= analysis.breakEven ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Acima do ponto de equilíbrio
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    Abaixo do ponto de equilíbrio
                  </>
                )}
              </div>
              {analysis.currentRevenue !== analysis.receivedRevenue && (
                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Receita vendida: {formatCurrency(analysis.currentRevenue)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cash Analysis Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Análise de Caixa
            </CardTitle>
            <CardDescription>
              Capacidade de operação baseada no saldo atual
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Saldo em Caixa</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(analysis.currentCashBalance)}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Burn Rate</p>
                <p className="text-xl font-bold text-orange-600">
                  {formatCurrency(analysis.burnRate)}
                </p>
                <p className="text-xs text-muted-foreground">/mês</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Runway</span>
                <span className={cn(
                  "text-xl font-bold",
                  runwayStatus === 'healthy' ? "text-green-600" : 
                  runwayStatus === 'warning' ? "text-yellow-600" : "text-red-600"
                )}>
                  {cashRunway === Infinity 
                    ? '∞' 
                    : `${cashRunway.toFixed(1)} meses`}
                </span>
              </div>
              <Progress 
                value={Math.min((cashRunway / 12) * 100, 100)} 
                className={cn(
                  "h-3 mt-2",
                  runwayStatus === 'healthy' ? "[&>div]:bg-green-500" : 
                  runwayStatus === 'warning' ? "[&>div]:bg-yellow-500" : "[&>div]:bg-red-500"
                )}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {runwayStatus === 'healthy' 
                  ? 'Posição saudável (6+ meses)' 
                  : runwayStatus === 'warning' 
                    ? 'Atenção - menos de 6 meses de caixa'
                    : 'Crítico - menos de 3 meses de caixa'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Indicadores de Risco
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Margem de Segurança</p>
                <p className={cn(
                  "text-2xl font-bold",
                  isSafe ? "text-green-600" : "text-yellow-600"
                )}>
                  {analysis.safetyMargin.toFixed(1)}%
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSafe ? 'Posição confortável' : 'Atenção necessária'}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Alavancagem Operacional</p>
                <p className={cn(
                  "text-2xl font-bold",
                  hasHighLeverage ? "text-orange-600" : "text-blue-600"
                )}>
                  {analysis.operationalLeverage.toFixed(2)}x
                </p>
                <p className="text-xs text-muted-foreground">
                  {hasHighLeverage ? 'Alta sensibilidade' : 'Risco moderado'}
                </p>
              </div>
            </div>

            {breakEvenImpact && (
              <div className="pt-4 border-t">
                <p className="text-sm font-medium mb-2">Simulação: +$10k em custos fixos</p>
                <div className="flex justify-between text-sm">
                  <span>Novo Break-even:</span>
                  <span className="font-medium text-orange-600">
                    {formatCurrency(breakEvenImpact.newBreakEven)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Aumento de {breakEvenImpact.percentageIncrease.toFixed(1)}%
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown Tables */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Custos com Pessoal</CardTitle>
            <CardDescription>
              Salários base são fixos • Comissões calculadas sobre recebido
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Salários Base</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(analysis.employees.fixed)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="bg-slate-100">Fixo</Badge>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="flex items-center gap-1">
                    Comissões ({(analysis.commissionRate * 100).toFixed(0)}%)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Sobre receita recebida: {formatCurrency(analysis.receivedRevenue)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(analysis.employees.variable)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="bg-teal-100">Variável</Badge>
                  </TableCell>
                </TableRow>
                <TableRow className="font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(analysis.employees.total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Despesas Recorrentes</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Fixo</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(analysis.recurring.fixed)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="bg-slate-100">Fixo</Badge>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Variável</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(analysis.recurring.variable)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary" className="bg-teal-100">Variável</Badge>
                  </TableCell>
                </TableRow>
                <TableRow className="font-bold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(analysis.recurring.total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </FinancePageLayout>
  );
}
